import type { ReactNode } from "react";

import { cn } from "@btnet/utilities-core";

export interface IShellNavItem {
  key: string;
  label: string;
  icon?: ReactNode;
  /**
   * Group label this item belongs under (e.g. "Financials"). Items are grouped by this value,
   * preserving first-seen group order; an item with no `group` falls back to a numbered
   * "Group N" bucket (matching `BTVerticalNav`'s original dummy-data behavior) so existing
   * callers that never set `group` are unaffected.
   */
  group?: string;
}

export interface IShellProps {
  /**
   * Left nav slot. Optional — omit (or pass `undefined`) to not render a sidebar, e.g. at a
   * breakpoint where the caller shows `bottombar` instead. A plain node: the caller builds it
   * (e.g. `<BTVerticalNav navItems={...} selectedKey={...} onSelectKey={...} />`) with whatever
   * nav-selection state it owns, rather than BTShellLayout supplying that state via a render
   * prop.
   */
  sidebar?: ReactNode;
  /** Main canvas slot. */
  children: ReactNode;
  /**
   * Bottom nav slot. Optional — omit (or pass `undefined`) to not render a bottombar.
   * BTShellLayout has no breakpoint logic of its own: which slot to populate at a given width is
   * entirely up to the caller (see `BTShellLayout.stories.tsx`'s `WithNav` for the
   * `useMediaQuery` pattern).
   */
  bottombar?: ReactNode;
  className?: string;
  "data-testid": string;
  /**
   * Render `children` with no shell around them: every wrapper becomes `display: contents` and
   * the slots, `className` and `data-testid` are dropped. The element tree stays the same, so a
   * caller that turns the shell on and off (e.g. per route) never remounts `children`.
   */
  chromeless?: boolean;
}

/**
 * Top-level layout shell for the research-preview information-architecture nav
 * (ado-285671, ado-286232). Purely presentational: `sidebar`/`bottombar`/`children` are plain
 * `ReactNode` slots arranged in a flex box. BTShellLayout owns no nav-selection state itself —
 * the caller builds `sidebar`/`bottombar` with whatever nav data/selection state it needs
 * (previously threaded in via a `renderSidebar`/`renderBottombar` render-prop pair that only
 * existed to hand the caller state BTShellLayout owned; lifting that state to the caller removed
 * the need for it).
 */
export function BTShellLayout({
  sidebar,
  bottombar,
  children,
  className,
  "data-testid": testid,
  chromeless = false,
}: IShellProps) {
  const pane = chromeless ? "contents" : "flex flex-1 flex-col overflow-hidden";
  return (
    <div
      data-testid={chromeless ? undefined : testid}
      className={
        chromeless
          ? "contents"
          : cn("flex h-dvh w-full overflow-hidden bg-white", className)
      }
    >
      {chromeless ? undefined : sidebar}
      <div className={pane}>
        <div className={pane}>{children}</div>
        {chromeless ? undefined : bottombar}
      </div>
    </div>
  );
}
